import { Box } from "@mui/material";
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import debounce from "debounce";
import "./DealersMap.css";
import { ISearchDealerResult } from "@/types/ISearchDealerResult";
import { getStateAbbreviation } from "@/utils/staticData/getStateAbbreviation";

interface DealersMapProps {
  apiKey?: string;
  dealers?: ISearchDealerResult[];
  userLocation?: { lat: number; lng: number } | null;
  selectedDealerId?: number | null;
  onMapCenterChange?: (latitude: number, longitude: number, bounds: google.maps.LatLngBounds | undefined) => void;
  shouldAutoCenter?: boolean;
  isLocationPermissionResolved?: boolean;
}

export interface DealersMapHandle {
  getCurrentBounds: () => google.maps.LatLngBounds | undefined;
  getCurrentCenter: () => { lat: number; lng: number } | null;
  setCenter: (lat: number, lng: number) => void;
  setZoom: (zoom: number) => void;
}

const DealersMap = forwardRef<DealersMapHandle, DealersMapProps>(
  ({ apiKey, dealers = [], userLocation, selectedDealerId, onMapCenterChange, shouldAutoCenter = true, isLocationPermissionResolved = false }, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowsRef = useRef<google.maps.InfoWindow[]>([]);
  const dragEndListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const zoomChangeListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const debouncedHandleMapCenterChangeRef = useRef<ReturnType<typeof debounce> | null>(null);
  const centeredOnUserRef = useRef(false);
  const isMapInitializedRef = useRef(false);
  const isScriptLoadingRef = useRef(false);

  const DEBOUNCE_TIMEOUT = 500;
  const defaultLat = 40.7128;
  const defaultLng = -74.006;
  const zoom = 12;

const pendingCenterRef = useRef<{ lat: number; lng: number } | null>(null);
const pendingZoomRef = useRef<number | null>(null);

useImperativeHandle(ref, () => ({
  getCurrentBounds: () => map?.getBounds(),
  getCurrentCenter: () => {
    const center = map?.getCenter();
    return center ? { lat: center.lat(), lng: center.lng() } : null;
  },
  setCenter: (lat: number, lng: number) => {
    if (map) map.setCenter({ lat, lng });
    else pendingCenterRef.current = { lat, lng };
  },
  setZoom: (z: number) => {
    if (map) map.setZoom(z);
    else pendingZoomRef.current = z;
  },
}), [map]);

useEffect(() => {
  if (!map) return;

  if (pendingCenterRef.current) {
    map.setCenter(pendingCenterRef.current);
    pendingCenterRef.current = null;
  }

  if (pendingZoomRef.current != null) {
    map.setZoom(pendingZoomRef.current);
    pendingZoomRef.current = null;
  }
}, [map]);

useEffect(() => {
  if (!map || !userLocation || !shouldAutoCenter) return;
  if (centeredOnUserRef.current) return;

  map.setCenter(userLocation);
  map.setZoom(9);
  centeredOnUserRef.current = true;
}, [map, userLocation, shouldAutoCenter]);

  useEffect(() => {
    if (!onMapCenterChange) {
      debouncedHandleMapCenterChangeRef.current = null;
      return;
    }

    debouncedHandleMapCenterChangeRef.current = debounce((mapInstance: google.maps.Map) => {
      const center = mapInstance.getCenter();
      const bounds: google.maps.LatLngBounds | undefined = mapInstance.getBounds();
      if (center) {
        const lat = center.lat();
        const lng = center.lng();
        onMapCenterChange(lat, lng , bounds);
      }
    }, DEBOUNCE_TIMEOUT);

    return () => {
      if (debouncedHandleMapCenterChangeRef.current) {
        debouncedHandleMapCenterChangeRef.current.clear?.();
      }
    };
  }, [onMapCenterChange]);

  useEffect(() => {
    if (!apiKey || !mapRef.current) return;
    
    if (isMapInitializedRef.current || map) return;

    const GOOGLE_SCRIPT_ID = "google-maps-js";

    const initializeMap = () => {
      if (isMapInitializedRef.current || !mapRef.current || !window.google?.maps) return;

      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: defaultLat, lng: defaultLng },
        zoom: zoom,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        disableDefaultUI: false,
        zoomControl: isLocationPermissionResolved,
        mapTypeControl: true,
        scaleControl: true,
        streetViewControl: true,
        rotateControl: true,
        fullscreenControl: true,
        draggable: isLocationPermissionResolved,
        gestureHandling: isLocationPermissionResolved ? 'auto' : 'none',
      });
      
      isMapInitializedRef.current = true;
      setMap(newMap);
    };

    if (window.google?.maps) {
      initializeMap();
      return;
    }

    const existing = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (!window.google?.maps) {
        existing.addEventListener("load", initializeMap, { once: true });
      } else {
        initializeMap();
      }
      return;
    }

    if (!isScriptLoadingRef.current) {
      isScriptLoadingRef.current = true;
      const script = document.createElement("script");
      script.id = GOOGLE_SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        isScriptLoadingRef.current = false;
        initializeMap();
      };
      script.onerror = () => {
        isScriptLoadingRef.current = false;
      };
      document.head.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, isLocationPermissionResolved, defaultLat, defaultLng]);

  useEffect(() => {
    if (!map) return;
    map.setOptions({
      draggable: isLocationPermissionResolved,
      gestureHandling: isLocationPermissionResolved ? 'auto' : 'none',
      zoomControl: isLocationPermissionResolved,
    });
  }, [map, isLocationPermissionResolved]);

  useEffect(() => {
    if (!map || !debouncedHandleMapCenterChangeRef.current) return;

    if (dragEndListenerRef.current) {
      google.maps.event.removeListener(dragEndListenerRef.current);
    }
    if (zoomChangeListenerRef.current) {
      google.maps.event.removeListener(zoomChangeListenerRef.current);
    }

    dragEndListenerRef.current = google.maps.event.addListener(map, 'dragend', () => {
      if (debouncedHandleMapCenterChangeRef.current) {
        debouncedHandleMapCenterChangeRef.current(map);
      }
    });

    zoomChangeListenerRef.current = google.maps.event.addListener(map, 'zoom_changed', () => {
      if (debouncedHandleMapCenterChangeRef.current) {
        debouncedHandleMapCenterChangeRef.current(map);
      }
    });

    return () => {
      if (dragEndListenerRef.current) {
        google.maps.event.removeListener(dragEndListenerRef.current);
      }
      if (zoomChangeListenerRef.current) {
        google.maps.event.removeListener(zoomChangeListenerRef.current);
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !dealers.length) return;

    markersRef.current.forEach((m) => m.setMap(null));
    infoWindowsRef.current = [];
    const newMarkers: google.maps.Marker[] = [];
    const newInfoWindows: google.maps.InfoWindow[] = [];

    const validDealers = dealers.filter((dealer) => {
      const lat = dealer.latitude;
      const lng = dealer.longitude;

      return lat != null && lng != null && !(lat === 0 && lng === 0);
    });

    validDealers.forEach((dealer) => {
      const lat = dealer.latitude;
      const lng = dealer.longitude;

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: map,
        title: dealer.companyName,
        label: undefined,
        icon: {
          url: 'data:image/svg+xml;utf8,<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="16" fill="%23FFCAA4"/><g transform="translate(9 7)"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.25 7.5C0.25 3.77208 3.27208 0.75 7 0.75C10.7279 0.75 13.75 3.77208 13.75 7.5C13.75 9.39463 12.8899 11.0552 11.7192 12.5753C10.741 13.8455 9.48875 15.0868 8.23798 16.3268L8.2371 16.3276C8.00085 16.5619 7.76464 16.796 7.53033 17.0303C7.23744 17.3232 6.76256 17.3232 6.46967 17.0303C6.2352 16.7959 5.9983 16.561 5.76187 16.3266C4.5111 15.0867 3.25903 13.8455 2.28079 12.5753C1.11012 11.0552 0.25 9.39463 0.25 7.5ZM9.25 7.5C9.25 8.74264 8.24264 9.75 7 9.75C5.75736 9.75 4.75 8.74264 4.75 7.5C4.75 6.25736 5.75736 5.25 7 5.25C8.24264 5.25 9.25 6.25736 9.25 7.5Z" fill="%23FF6B00"/></g></svg>',
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 32),
        },
      });
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="min-width:220px;max-width:260px;padding:18px 12px 0px 12px;background:#fff;border-radius:16px;box-shadow:0 4px 24px rgba(0,0,0,0.10);font-family:'Mulish',sans-serif;">
            <div style="display:flex;flex-wrap:wrap;justify-content:space-between;align-items:flex-start;margin-bottom:6px;">
              <div style="font-weight:700;font-size:0.95rem;color:#222;line-height:1.2;min-width:0;max-width:calc(100% - 70px);flex-shrink:1;word-break:break-word;">
                ${dealer.companyName}
              </div>
              <div style="font-size:0.9rem;color:#888;font-weight:400;margin-top:2px;min-width:60px;flex-shrink:0;text-align:right;">
                ${dealer.distance?.toFixed(1)} mi
              </div>
            </div>
            <div style="color:#888;font-size:0.95rem;font-weight:600;letter-spacing:0.5px;margin-bottom:16px;text-transform:uppercase;">
              ${dealer.streetAddress}<br>${dealer.city}, ${getStateAbbreviation(dealer.state)} ${dealer.zipCode ?? ""}
            </div>
            <div style="text-align:center;font-weight:700;font-size:0.9rem;color:#222;cursor:pointer;padding-bottom:16px;" onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${
              dealer.latitude
            },${dealer.longitude}','_blank')">
              Directions
            </div>
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindowsRef.current.forEach((iw) => iw.close());
        infoWindow.open(map, marker);
      });

      newMarkers.push(marker);
      newInfoWindows.push(infoWindow);
    });

    markersRef.current = newMarkers;
    infoWindowsRef.current = newInfoWindows;

    if (shouldAutoCenter && newMarkers.length > 0 && map) {
      if (newMarkers.length === 1) {
        map.setCenter(newMarkers[0].getPosition()!);
        map.setZoom(14);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        newMarkers.forEach((m) => bounds.extend(m.getPosition()!));
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        const listener = google.maps.event.addListener(map, 'idle', () => {
          const z = map.getZoom();
          if (z != null && z > 14) map.setZoom(14);
          google.maps.event.removeListener(listener);
        });
      }
    }
  }, [map, dealers, shouldAutoCenter]);

  useEffect(() => {
    if (!map || selectedDealerId == null) return;
    const index = dealers.findIndex((d) => d.id === selectedDealerId);
    if (index >= 0 && markersRef.current[index] && infoWindowsRef.current[index]) {
      const marker = markersRef.current[index];
      infoWindowsRef.current.forEach((iw) => iw.close());
      map.panTo(marker.getPosition()!);
      infoWindowsRef.current[index].open(map, marker);
    }
  }, [map, selectedDealerId, dealers]);

  if (!apiKey) {
    return (
      <Box
        sx={{
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #ddd",
          }}
        >
          <Box sx={{ textAlign: "center", color: "#666" }}>
            <Box sx={{ fontSize: "48px", mb: 1 }}>🗺️</Box>
            <Box sx={{ fontSize: "14px", fontWeight: 500 }}>Dealers Map</Box>
            <Box sx={{ fontSize: "12px", mt: 0.5, mb: 0.5 }}>
              Add your Google Maps API key to display the map
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className="FindDealer-map-box"
      sx={{
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        ref={mapRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    </Box>
  );
});

DealersMap.displayName = "DealersMap";

export default DealersMap;
